import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Stack,
  Fade,
  CircularProgress,
} from '@mui/material'
import localization from '../utils/localization'
import ContentUtils from '../utils/content'
import ContentIcon from './ContentIcon'
import PasswordForm from '../components/PasswordForm'
import ServerUtils from '../utils/server'
import AesUtils from '../utils/aes'
import toast from 'react-hot-toast'
import UserUtils from '../utils/user'
import TextForm from '../components/TextForm'
import TotpForm from '../components/TotpForm'
import BlockchainForm from '../components/BlockchainForm'

function ModalContent(props) {
  const [submit, setSubmit] = useState(false)
  const passwordFormRef = useRef()
  const textFormRef = useRef()
  const totpFormRef = useRef()
  const blockchainFormRef = useRef()

  function submitContent() {
    if (submit) return
    setSubmit(true)
    let contentData = {}
    switch (props.contentType) {
      case ContentUtils.getPasswordType():
        if (passwordFormRef.current.checkForm()) {
          setSubmit(false)
          return
        }
        contentData = passwordFormRef.current.getContentData()
        break
      case ContentUtils.getTextType():
        if (textFormRef.current.checkForm()) {
          setSubmit(false)
          return
        }
        contentData = textFormRef.current.getContentData()
        break
      case ContentUtils.getTotpType():
        if (totpFormRef.current.checkForm()) {
          setSubmit(false)
          return
        }
        contentData = totpFormRef.current.getContentData()
        break
      case ContentUtils.getBlockChainType():
        if (blockchainFormRef.current.checkForm()) {
          setSubmit(false)
          return
        }
        contentData = blockchainFormRef.current.getContentData()
        break
      default:
        break
    }
    contentData.tags = []
    if (props.contentInfo.id) {
      contentData.tags = props.contentInfo.tags
    } else if (props.tag) {
      contentData.tags.push(props.tag)
    }
    let userInfo = UserUtils.getUserInfo()
    if (props.contentInfo.id) {
      ServerUtils.updateContent({
        content_id: props.contentInfo.id,
        version: userInfo.version,
        encrypted_data: AesUtils.encryptData(
          userInfo.account,
          UserUtils.getMasterPass(),
          JSON.stringify(contentData)
        ),
      }).then(async (resp) => {
        setSubmit(false)
        if (!resp.ok) {
          try {
            let respJson = await resp.json()
            if (respJson.errno !== 0) {
              toast.error(ServerUtils.handleError(respJson))
              return
            }
          } catch (e) {}
          toast.error(localization.strings.networkErr)
          return
        }
        let respJson = await resp.json()
        if (respJson.errno !== 0) {
          toast.error(ServerUtils.handleError(respJson))
          return
        }
        ContentUtils.updateContent(
          Object.assign(
            {
              id: props.contentInfo.id,
              version: userInfo.version,
              last_modify_time: new Date().getTime() / 1000,
            },
            contentData
          )
        )
        toast.success(localization.strings.successModify)
        props.onClose()
      })
    } else {
      ServerUtils.createContent({
        version: userInfo.version,
        encrypted_data: AesUtils.encryptData(
          userInfo.account,
          UserUtils.getMasterPass(),
          JSON.stringify(contentData)
        ),
      }).then(async (resp) => {
        setSubmit(false)
        if (!resp.ok) {
          try {
            let respJson = await resp.json()
            if (respJson.errno !== 0) {
              toast.error(ServerUtils.handleError(respJson))
              return
            }
          } catch (e) {}
          toast.error(localization.strings.networkErr)
          return
        }
        let respJson = await resp.json()
        if (respJson.errno !== 0) {
          toast.error(ServerUtils.handleError(respJson))
          return
        }
        ContentUtils.addContent(
          Object.assign(
            {
              id: respJson.data.content_id,
              version: userInfo.version,
              last_modify_time: new Date().getTime() / 1000,
            },
            contentData
          )
        )
        toast.success(localization.strings.successCreate)
        props.onClose()
        props.onCreate(
          Object.assign(
            {
              id: respJson.data.content_id,
              version: userInfo.version,
              last_modify_time: new Date().getTime() / 1000,
            },
            contentData
          )
        )
      })
    }
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          p: 1,
          overflow: 'visible',
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          pt: 4,
          pb: 1,
          px: 4,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            backgroundColor: 'rgba(30, 136, 229, 0.1)',
            color: '#1E88E5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ContentIcon contentType={props.contentType} size="1.8rem" fontSize="1.4rem" />
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: '#1E293B',
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {props.title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 4 }}>
        <Fade in={props.open} timeout={500}>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 4 }}>
              {props.contentType === ContentUtils.getPasswordType() && (
                <PasswordForm ref={passwordFormRef} contentInfo={props.contentInfo} />
              )}
              {props.contentType === ContentUtils.getTextType() && (
                <TextForm ref={textFormRef} contentInfo={props.contentInfo} />
              )}
              {props.contentType === ContentUtils.getTotpType() && (
                <TotpForm ref={totpFormRef} contentInfo={props.contentInfo} />
              )}
              {props.contentType === ContentUtils.getBlockChainType() && (
                <BlockchainForm ref={blockchainFormRef} contentInfo={props.contentInfo} />
              )}
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="text"
                onClick={props.onClose}
                disabled={submit}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                }}
              >
                {localization.strings.cancel}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={submitContent}
                disabled={submit}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  backgroundColor: '#1E88E5',
                  boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#1565C0',
                    boxShadow: '0 12px 24px rgba(30, 136, 229, 0.3)',
                  },
                }}
              >
                {submit ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  localization.strings.confirm
                )}
              </Button>
            </Stack>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  )
}

export default ModalContent
